import { TryOnWidget } from "./widget"
import { PURCHASE_SELECTOR } from "./discovery"
import { keepPlaced } from "./placement"
import { resolveLang, STRINGS } from "./strings"
import type { ProductContext, VisualCommerceApi, WidgetConfig } from "./types"

const script = document.currentScript as HTMLScriptElement | null

// Compiled-in default API base (esbuild --define). Production builds bake the
// prod API; the dev build bakes localhost. This is the fallback for adapters
// that can neither set data-* attributes nor control the script URL — e.g. a
// loader uploaded to and served by Tiendanube's own CDN.
declare const __API_BASE__: string
const DEFAULT_API_BASE = typeof __API_BASE__ === "string" ? __API_BASE__ : "http://localhost:3000"

// Adapters that inject the loader via a platform Script Tag API (e.g. Tiendanube)
// cannot set data-* attributes, so tenant-agnostic config rides in the script
// URL's query string. Precedence: explicit data-attribute > script URL query.
function fromScriptQuery(key: string): string | undefined {
  try {
    return new URL(script?.src ?? "").searchParams.get(key) ?? undefined
  } catch {
    return undefined
  }
}

function boot() {
  if (window.VisualCommerce) return
  if (!customElements.get("vc-try-on")) customElements.define("vc-try-on", TryOnWidget)

  // data-lang on the embed > script URL ?lang= > the page's <html lang> > "es".
  const lang = resolveLang(
    script?.dataset.lang || fromScriptQuery("lang"),
    document.documentElement.lang
  )

  const config: WidgetConfig = {
    apiBase: (script?.dataset.apiBase || fromScriptQuery("apiBase") || DEFAULT_API_BASE).replace(/\/$/, ""),
    // Tenancy is origin-based (ADR 0001): the backend resolves the merchant from
    // the request Origin. The embed carries no merchant key.
    target: script?.dataset.target,
    lang,
    ctaLabel: script?.dataset.ctaLabel || fromScriptQuery("ctaLabel") || STRINGS[lang].ctaDefault,
    primaryColor: script?.dataset.primaryColor || "#1f2937",
    sizeEnabled: script?.dataset.sizeEnabled !== "false",
    // Resolved from the boot handshake (size_delivered_with_tryon); false until then.
    sizeCombined: false,
    getProduct: window.VisualCommerceConfig?.getProduct
  }

  const widget = document.createElement("vc-try-on") as TryOnWidget
  widget.configure(config)
  const replace = keepPlaced(widget, config.target)
  // Runs after the widget's own listener has re-scoped it to the new product.
  window.addEventListener("visual-commerce:product-change", replace)

  const api: VisualCommerceApi = {
    open: () => widget.open(),
    close: () => widget.close(),
    refreshProduct: () => {
      const product = widget.refreshProduct()
      replace()
      return product
    },
    getProduct: () => widget.getProduct(),
    trackAddToCart: (product) => widget.trackAddToCart(product)
  }
  window.VisualCommerce = api

  bindCartTracking(widget)
}

// Auto-emit the add_to_cart funnel event when the store's purchase control is
// clicked. Reuses the same selector that anchors placement, so it works across
// themes with no per-store code. Delegated from the document so buttons rendered
// after boot count too; capture phase so a theme's own handler that stops
// propagation (or navigates) can't swallow the event first.
function bindCartTracking(widget: TryOnWidget) {
  document.addEventListener("click", (event) => {
    if ((event.target as Element | null)?.closest?.(PURCHASE_SELECTOR)) widget.trackAddToCart()
  }, { capture: true, passive: true })
}

if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot, { once: true })
else boot()

export type { ProductContext }
